import { normalizeMediaType } from './mediaType';
import type { MediaType } from './mediaType';

/**
 * Copy that names the kind of asset being bought or selected, so a Video download asks for videos
 * rather than "photos". Keyed by the photo-worded label, which doubles as the photo translation
 * key — a product with no media type of its own sells photos, so prints, packages and
 * studio-fulfilled digitals need no row of their own.
 */
const LABEL_KEYS = {
    addPhotos: { slideshow: 'addSlideshows', video: 'addVideos' },
    allAvailablePhotos: { slideshow: 'allAvailableSlideshows', video: 'allAvailableVideos' },
    // Digital download scope options — see OptionSelect.
    allPhotosInAlbum: { slideshow: 'allSlideshowsInAlbum', video: 'allVideosInAlbum' },
    allPhotosInGallery: { slideshow: 'allSlideshowsInGallery', video: 'allVideosInGallery' },
    buyPhoto: { slideshow: 'buySlideshow', video: 'buyVideo' },
    // Singular, and one character away from `selectPhotos` below: this one labels a single
    // package builder tile asking for one asset, that one is the Store's Select Photos heading.
    deselectPhoto: { slideshow: 'deselectSlideshow', video: 'deselectVideo' },
    // Package item detail summary line — see spPackageItemDetail. Named for the digital
    // download scope it labels: one asset, every photo in the album, or every photo in the
    // gallery.
    individualPhotosDigitals: {
        slideshow: 'individualSlideshowsDigitals',
        video: 'individualVideosDigitals'
    },
    photoSelected: { slideshow: 'slideshowSelected', video: 'videoSelected' },
    photosInAlbum: { slideshow: 'slideshowsInAlbum', video: 'videosInAlbum' },
    photosInGallery: { slideshow: 'slideshowsInGallery', video: 'videosInGallery' },
    photosSelected: { slideshow: 'slideshowsSelected', video: 'videosSelected' },
    selectPhoto: { slideshow: 'selectSlideshow', video: 'selectVideo' },
    selectPhotos: { slideshow: 'selectSlideshows', video: 'selectVideos' },
    singlePhoto: { slideshow: 'singleSlideshow', video: 'singleVideo' },
    // The package builder grid's hover button, opening the asset rather than selecting it.
    viewPhoto: { slideshow: 'viewSlideshow', video: 'viewVideo' }
} satisfies Record<string, Record<Exclude<MediaType, 'photo'>, string>>;

export type MediaLabel = keyof typeof LABEL_KEYS;

/** Translation key for one piece of copy, for the media type being bought or selected. */
export const getMediaLabelKey = (
    label: MediaLabel,
    mediaType: Nullable<string> | undefined
): string => {
    const type = normalizeMediaType(mediaType);

    return type === 'photo' ? label : LABEL_KEYS[label][type];
};

/**
 * Whole sentences, one per ordered pair of media types, shown when the asset a client picked is
 * not the kind the product they picked it for sells.
 *
 * A mapped type rather than a template literal key: `Exclude<MediaType, S>` admits exactly the six
 * keys the translation table ships and rejects `photo → photo` at compile time, which is the bug
 * case — a mismatch message has no business being reachable when the two types agree.
 */
const MISMATCH_KEYS = {
    photo: {
        slideshow: 'mediaTypeNotSupportedPhotoForSlideshow',
        video: 'mediaTypeNotSupportedPhotoForVideo'
    },
    slideshow: {
        photo: 'mediaTypeNotSupportedSlideshowForPhoto',
        video: 'mediaTypeNotSupportedSlideshowForVideo'
    },
    video: {
        photo: 'mediaTypeNotSupportedVideoForPhoto',
        slideshow: 'mediaTypeNotSupportedVideoForSlideshow'
    }
} satisfies { [S in MediaType]: { [T in Exclude<MediaType, S>]: string } };

/**
 * Translation key explaining that `selectedMediaType` cannot be sold as a product that sells
 * `supportedMediaType`. Two agreeing types have nothing to explain, and the table ships no row
 * for them, so they read back as `undefined`.
 */
export const getMediaTypeMismatchKey = (
    selectedMediaType: MediaType,
    supportedMediaType: MediaType
): string | undefined =>
    (MISMATCH_KEYS[selectedMediaType] as Partial<Record<MediaType, string>>)[supportedMediaType];
